import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Stack,
} from '@mui/material';
import {
  Timeline as TimelineIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  ShoppingCart as OrderIcon,
} from '@mui/icons-material';
import UnifiedTimelineModal from './UnifiedTimelineModal';
import { UnifiedTimelineData } from '../../types/timeline';
import customerTimelineService from '../../services/customerTimelineService';

const TimelineDemo: React.FC = () => {
  const [showTimeline, setShowTimeline] = useState(false);
  const [timelineData, setTimelineData] = useState<UnifiedTimelineData | null>(null);
  const [timelineType, setTimelineType] = useState<'customer' | 'site' | 'order'>('customer');

  const handleTimelineView = async (type: 'customer' | 'site' | 'order') => {
    setTimelineType(type);
    
    try {
      let data: UnifiedTimelineData;
      
      switch (type) {
        case 'customer':
          const customerData = await customerTimelineService.getCustomerWithTimelineData('customer-1');
          data = { type: 'customer', customer: customerData };
          break;
        case 'site':
          const siteData = await customerTimelineService.getSiteWithTimelineData('site-1');
          data = { type: 'site', site: siteData };
          break;
        case 'order':
          const orderData = await customerTimelineService.getOrderWithTimelineData('order-1');
          data = { type: 'order', order: orderData };
          break;
        default:
          return;
      }
      
      setTimelineData(data);
      setShowTimeline(true);
    } catch (error) {
      console.error('Failed to load timeline data:', error);
      alert('Failed to load timeline data. Please ensure the API services are available.');
    }
  };

  const getTimelineDescription = (type: string) => {
    switch (type) {
      case 'customer':
        return 'View customer details with sites, orders, products, items, and timeline progression';
      case 'site':
        return 'View site details with orders, products, items, and timeline progression';
      case 'order':
        return 'View order details with products, items, and timeline progression';
      default:
        return '';
    }
  };

  const getTimelineIcon = (type: string) => {
    switch (type) {
      case 'customer':
        return <BusinessIcon />;
      case 'site':
        return <LocationIcon />;
      case 'order':
        return <OrderIcon />;
      default:
        return <TimelineIcon />;
    }
  };

  const getTimelineColor = (type: string) => {
    switch (type) {
      case 'customer':
        return 'primary';
      case 'site':
        return 'secondary';
      case 'order':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Unified Timeline System Demo
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Experience the unified timeline navigation across different data types. 
        Each timeline provides a consistent flow from high-level data to detailed item timelines.
      </Typography>

      <Grid container spacing={3}>
        {(['customer', 'site', 'order'] as const).map((type) => (
          <Grid item xs={12} md={4} key={type}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
            >
              <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      bgcolor: `${getTimelineColor(type)}.light`,
                      color: `${getTimelineColor(type)}.contrastText`,
                      mr: 2,
                    }}
                  >
                    {getTimelineIcon(type)}
                  </Box>
                  <Typography variant="h6" component="h2" sx={{ textTransform: 'capitalize' }}>
                    {type} Timeline
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, flex: 1 }}>
                  {getTimelineDescription(type)}
                </Typography>

                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <Chip
                    label={`${type === 'customer' ? 'Sites' : type === 'site' ? 'Orders' : 'Products'} → Items → Timeline`}
                    size="small"
                    color={getTimelineColor(type) as any}
                    variant="outlined"
                  />
                </Stack>

                <Button
                  variant="contained"
                  color={getTimelineColor(type) as any}
                  startIcon={<TimelineIcon />}
                  onClick={() => handleTimelineView(type)}
                  fullWidth
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  Open {type.charAt(0).toUpperCase() + type.slice(1)} Timeline
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Timeline Modal */}
      {timelineData && (
        <UnifiedTimelineModal
          isOpen={showTimeline}
          onClose={() => {
            setShowTimeline(false);
            setTimelineData(null);
          }}
          data={timelineData}
          sidePanelMode={true}
        />
      )}

      {/* Features Section */}
      <Box sx={{ mt: 6 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          Key Features
        </Typography>
        
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Unified Navigation
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Consistent navigation flow across all data types with intuitive back navigation
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Real-time Updates
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Live status updates and progress tracking with visual indicators
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Responsive Design
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Mobile-friendly layout with adaptive panel sizing and touch interactions
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  QR Code Integration
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  QR code display and tracking for individual items with mobile support
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default TimelineDemo;